<script setup>
/**
 * Composant racine de l'application
 * Gère les éléments globaux comme les toasts
 */
import { onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import AppToaster from '@/components/ui/AppToaster.vue'
import UpdateAvailableToast from '@/components/ui/UpdateAvailableToast.vue'
import AppConfirm from '@/components/ui/AppConfirm.vue'

const router = useRouter()
const authStore = useAuthStore()

// Vérifier l'authentification au démarrage.
// - Supabase : le middleware API renouvelle l'access token via les cookies
//   HTTPOnly de manière transparente.
// - Google : on déclenche un refresh proactif si l'access provider_token
//   est expiré mais qu'on dispose encore du refresh token. Cela permet à
//   l'utilisateur de retrouver une session Google Calendar opérationnelle
//   même après plusieurs jours d'absence, sans nouvelle consent screen.
onMounted(async () => {
  if (authStore.isAuthenticated) {
    await authStore.checkAuthStatus()
  }
  // Tente un refresh Google même hors auth Supabase, au cas où le
  // refresh token Google a survécu dans le localStorage entre 2 sessions.
  await authStore.bootGoogleTokenIfNeeded?.()
})

// Garde aussi un router router import explicite pour usage futur
void router
</script>

<template>
  <router-view v-slot="{ Component }">
    <transition name="page" mode="out-in">
      <component :is="Component" />
    </transition>
  </router-view>
  
  <!-- Notifications toast modernes (système réutilisable, voir useNotifier) -->
  <AppToaster />

  <!-- Détection automatique d'une nouvelle version déployée -->
  <UpdateAvailableToast />

  <!-- Compatibilité : Toast PrimeVue pour les composants tiers encore non migrés -->
  <Toast position="top-right" />

  <!-- Dialog de confirmation global -->
  <ConfirmDialog />

  <!-- Modale de confirmation globale (voir useConfirmModal) -->
  <AppConfirm />
</template>

<style>
/* Transitions de page */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
